import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useParams, Redirect } from 'react-router-dom';
import { Seo, AdSenseUnit, Loading } from '../shared';
import { AD_SLOTS } from '../../utils/adsense';
import ExploreHero from '../explore/ExploreHero';
import QuickFactsBar from '../explore/QuickFactsBar';
import OfficialCitation from '../explore/OfficialCitation';
import { TrekDaysSection } from './JourneyStepSection';
import JourneyDashboard from './JourneyDashboard';
import GuidedStepPanel from './GuidedStepPanel';
import JourneyTimelinePanel from './JourneyTimelinePanel';
import JourneyBudgetPanel from './JourneyBudgetPanel';
import DocumentsWalletPanel from './DocumentsWalletPanel';
import JourneyResourcesPanel from './JourneyResourcesPanel';
import JourneyWeatherPanel from './JourneyWeatherPanel';
import JourneyFaqPanel from './JourneyFaqPanel';
import JourneyEmergencyPanel from './JourneyEmergencyPanel';
import PackingChecklistPanel from './PackingChecklistPanel';
import { loadJourneyBySlug } from '../../utils/cmsContentLoader';
import {
	loadJourneyProgress, setActiveStep, getOverallProgress,
} from '../../data/journeys/journeyProgress';
import { LAST_VERIFIED } from '../../data/officialSources';
import '../../styles/explore.scss';
import '../../styles/journey.scss';

import JourneyPanelSwitcher from './JourneyPanelSwitcher';
import JourneyBookingSection from '../booking/JourneyBookingSection';

function trekPanelMeta(journey) {
	const dayCount = journey.trekDays?.length || 0;
	const duration = journey.quickFacts?.find((f) => f.label === 'Duration')?.value;
	const region = journey.region || '';

	let title = 'Day-by-day trek';
	if (region.includes('Pilgrimage') || region.includes('culture')) {
		title = 'Day-by-day itinerary';
	} else if (region.includes('Wildlife') || region.includes('lowland')) {
		title = 'Day-by-day safari';
	} else if (region.includes('Kathmandu Valley')) {
		title = 'Day-by-day walk';
	}

	const parts = [];
	if (duration) parts.push(duration);
	if (dayCount) parts.push(`${dayCount} ${dayCount === 1 ? 'day' : 'days'} outlined`);

	const intro = parts.length
		? `${parts.join(' · ')} — ${journey.subtitle || journey.lead}`
		: (journey.subtitle || journey.lead || 'On-the-ground reference for this journey.');

	return { title, intro, dayCount };
}

const PANEL_HINTS = {
	timeline: 'Preparation schedule — does not change your current trip step.',
	budget: 'Cost breakdown for this trek.',
	documents: 'Documents to gather — separate from step tasks.',
	packing: 'Gear checklist for this trek.',
	trek: 'Day-by-day trail reference.',
	weather: 'Seasonal weather patterns.',
	emergency: 'Altitude, evacuation, and emergency numbers.',
	resources: 'Maps and offline downloads.',
	faq: 'Common questions about this trek.',
};

const ONBOARDING_KEY = 'guided-journey-onboarding-seen';

function GuidedJourneyPage() {
	const { slug } = useParams();
	const [journey, setJourney] = useState(null);
	const [loading, setLoading] = useState(true);
	const [progress, setProgress] = useState(() => loadJourneyProgress(slug));
	const [activePanel, setActivePanel] = useState('trek');
	const prepSectionRef = useRef(null);
	const [showOnboarding, setShowOnboarding] = useState(() => {
		try {
			return !localStorage.getItem(ONBOARDING_KEY);
		} catch {
			return false;
		}
	});
	const [activeStepId, setActiveStepId] = useState('dream');

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		loadJourneyBySlug(slug).then((loaded) => {
			if (cancelled) return;
			setJourney(loaded);
			const saved = loadJourneyProgress(slug);
			setProgress(saved);
			setActiveStepId(saved.activeStepId || loaded?.steps?.[0]?.id || 'dream');
			setLoading(false);
		});
		return () => { cancelled = true; };
	}, [slug]);

	const handlePanelChange = useCallback((panelId) => {
		setActivePanel(panelId);
		document.querySelector('.guided-journey__main')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}, []);

	const scrollToPrep = useCallback(() => {
		requestAnimationFrame(() => {
			prepSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		});
	}, []);

	const dismissOnboarding = useCallback(() => {
		setShowOnboarding(false);
		try {
			localStorage.setItem(ONBOARDING_KEY, '1');
		} catch {
			// ignore storage errors
		}
	}, []);

	const handleSelectStep = useCallback((stepId) => {
		setActiveStepId(stepId);
		const updated = setActiveStep(slug, stepId);
		setProgress(updated);
		scrollToPrep();
		requestAnimationFrame(() => {
			document.getElementById(stepId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		});
	}, [slug, scrollToPrep]);

	const handleNextStep = useCallback(() => {
		if (!journey) return;
		const idx = journey.steps.findIndex((s) => s.id === activeStepId);
		if (idx >= 0 && idx < journey.steps.length - 1) {
			handleSelectStep(journey.steps[idx + 1].id);
		}
	}, [journey, activeStepId, handleSelectStep]);

	if (loading) return <Loading />;

	if (!journey) {
		return <Redirect to="/journeys" />;
	}

	const activeStep = journey.steps.find((s) => s.id === activeStepId) || journey.steps[0];
	const activeIndex = journey.steps.findIndex((s) => s.id === activeStep.id);
	const overallPercent = getOverallProgress(journey, progress);
	const trekPanel = trekPanelMeta(journey);

	return (
		<div className="explore-page guided-journey-page">
			<Seo
				title={journey.seoTitle || `${journey.title} — Guided Journey & Trek Planner`}
				path={`/journeys/${journey.slug}`}
				description={journey.seoDescription || journey.lead || journey.subtitle || `Step-by-step ${journey.title} planner — permits, budget, packing, and day-by-day itinerary for Nepal.`}
			/>

			<ExploreHero
				image={journey.hero}
				eyebrow="Guided Journey"
				title={journey.title}
				lead={journey.lead || journey.subtitle}
				compact
			/>

			<QuickFactsBar facts={journey.quickFacts} />

			<div className="container guided-journey__citation-wrap">
				<OfficialCitation
					sourceName={journey.primarySource?.name || 'Nepal Tourism Board'}
					sourceUrl={journey.primarySource?.url || 'https://ntb.gov.np'}
					lastVerified={journey.lastVerified || LAST_VERIFIED}
					subjectToChange
				>
					<p className="mb-0">Permit fees cited from DNPWC and NTB. Progress saves locally on your device.</p>
				</OfficialCitation>
			</div>

			<div className="guided-journey__layout container">
				<main className="guided-journey__main">
					<JourneyPanelSwitcher
						activePanel={activePanel}
						activeStep={activeStep}
						onPanelChange={handlePanelChange}
						onGoToPrep={scrollToPrep}
					/>

					{PANEL_HINTS[activePanel] && (
						<div className="guided-journey__panel-context">
							<p className="guided-journey__panel-context-hint">{PANEL_HINTS[activePanel]}</p>
						</div>
					)}

					{activePanel === 'timeline' && (
						<JourneyTimelinePanel
							timeline={journey.preparationTimeline}
							onGoToStep={handleSelectStep}
							activeStepId={activeStepId}
						/>
					)}

					{activePanel === 'budget' && (
						<JourneyBudgetPanel budget={journey.budget} />
					)}

					{activePanel === 'documents' && (
						<DocumentsWalletPanel journey={journey} progress={progress} onProgressUpdate={setProgress} />
					)}

					{activePanel === 'packing' && (
						<PackingChecklistPanel journey={journey} progress={progress} onProgressUpdate={setProgress} />
					)}

					{activePanel === 'trek' && (
						<div className="guided-panel guided-panel--trek">
							<div className="guided-panel__head">
								<div>
									<h3 className="guided-panel__title">{trekPanel.title}</h3>
									<p className="guided-panel__intro">{trekPanel.intro}</p>
								</div>
								<span className="guided-panel__stat">{trekPanel.dayCount || '—'}</span>
							</div>
							<TrekDaysSection trekDays={journey.trekDays} />
						</div>
					)}

					{activePanel === 'weather' && (
						<JourneyWeatherPanel weather={journey.weatherByMonth} />
					)}

					{activePanel === 'emergency' && (
						<JourneyEmergencyPanel
							altitudeSickness={journey.altitudeSickness}
							helicopterEvacuation={journey.helicopterEvacuation}
							emergencyContacts={journey.emergencyContacts}
							porterGuide={journey.porterGuide}
							licensedAgencies={journey.licensedAgencies}
						/>
					)}

					{activePanel === 'resources' && (
						<JourneyResourcesPanel journey={journey} />
					)}

					{activePanel === 'faq' && (
						<JourneyFaqPanel faqs={journey.faqs} travelerFaqs={journey.travelerFaqs} />
					)}

					<AdSenseUnit slot={AD_SLOTS.journey} className="adsense-unit--journey" layout="guided-journey" />

					<section
						ref={prepSectionRef}
						id="journey-prep"
						className="guided-journey__prep"
						aria-labelledby="journey-prep-heading"
					>
						<div className="guided-journey__prep-head">
							<div>
								<p className="guided-journey__prep-eyebrow">Before you go</p>
								<h2 id="journey-prep-heading" className="guided-journey__prep-title">Trip preparation checklist</h2>
								<p className="guided-journey__prep-lead">
									Work through visa, flights, permits, and packing here. Your day-by-day reference above stays open — nothing gets replaced.
								</p>
							</div>
							<p className="guided-journey__prep-meta">
								Step {activeIndex + 1} of {journey.steps.length} · {activeStep.name} · {overallPercent}% prepared
							</p>
						</div>

						{showOnboarding && (
							<div className="guided-journey__onboarding" role="status">
								<p>
									<strong>Tip:</strong> use the chips above for Day-by-day, Packing, and Weather.
									Scroll down here when you are ready to tick off planning steps.
								</p>
								<button type="button" className="guided-journey__onboarding-dismiss" onClick={dismissOnboarding}>
									Got it
								</button>
							</div>
						)}

						<JourneyDashboard
							journey={journey}
							progress={progress}
							activeStepId={activeStepId}
							onSelectStep={handleSelectStep}
							overallPercent={overallPercent}
							compact
						/>

						<GuidedStepPanel
							journey={journey}
							step={activeStep}
							stepIndex={activeIndex}
							totalSteps={journey.steps.length}
							progress={progress}
							onProgressUpdate={setProgress}
							onNext={handleNextStep}
						/>
					</section>
				</main>
			</div>

			<section className="discovery-section discovery-section--alt">
				<div className="container">
					<p className="guided-journey__footer-meta">
						Journey coverage: <strong>{journey.coverage}%</strong> — benchmark journey template
						{' · '}
						<Link to="/journeys">All journeys</Link>
						{' · '}
						<Link to={{ pathname: '/', hash: '#plan' }}>Plan topics</Link>
					</p>
				</div>
			</section>

			<JourneyBookingSection journey={journey} />
		</div>
	);
}

export default GuidedJourneyPage;
